import {Injectable} from '@angular/core';
import {BehaviorSubject, forkJoin, Observable, of, throwError} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';
import {ToastrService} from 'ngx-toastr';
import {
  DonneesChampModel,
  DonneesHistoriqueRow,
  DonneesImportFormat,
  DonneesListeValeur,
  DonneesRegleModel,
  DonneesExportFormat,
  DonneesUtilisationFlags
} from './parametre-donnees.types';
import {ParametreDonneesApiService} from './parametre-donnees-api.service';
import {
  auditDtoToRow,
  champToParamTablePayload,
  flagsToUsageDto,
  generalToSettingsDto,
  paramRuleDtoToModel,
  paramTableDtoToChamp,
  regleToDto,
  settingsDtoToGeneral,
  usageDtoToFlags
} from './parametre-donnees-mappers';
import {getParametrageSessionIds} from './parametre-donnees-session.util';
import {
  DataExportDestinationDto,
  DataExportFormatDto,
  DataImportFormatDto,
  RefDataModuleDto,
  resolveRefModuleTableDbId,
  SelectParamOptionDto,
  SelectParamsPutDto
} from './parametre-donnees-api.models';

function cloneChamps(list: DonneesChampModel[]): DonneesChampModel[] {
  return JSON.parse(JSON.stringify(list));
}

const ROUTE_KEY_TO_REF_DATA_MODULE_CODE: Record<string, string> = {
  produits: 'products',
  produit: 'products',
  product: 'products'
};

@Injectable({ providedIn: 'root' })
export class ParametreDonneesStateService {
  private readonly champs = new BehaviorSubject<Record<string, DonneesChampModel[]>>({});
  private readonly regles = new BehaviorSubject<Record<string, DonneesRegleModel[]>>({});
  private readonly historique = new BehaviorSubject<Record<string, DonneesHistoriqueRow[]>>({});
  private readonly utilisationModule = new BehaviorSubject<Record<string, DonneesUtilisationFlags>>({});
  private readonly importFormats = new BehaviorSubject<Record<string, DonneesImportFormat[]>>({});
  private readonly exportFormats = new BehaviorSubject<Record<string, DonneesExportFormat[]>>({});
  private readonly exportDestinations = new BehaviorSubject<Record<string, DataExportDestinationDto[]>>({});

  private refModules$ = new BehaviorSubject<RefDataModuleDto[] | null>(null);
  champs$ = this.champs.asObservable();

  constructor(
    private readonly api: ParametreDonneesApiService,
    private readonly toastr: ToastrService
  ) {}

  ensureRefModules(): Observable<RefDataModuleDto[]> {
    const cur = this.refModules$.value;
    if (cur && cur.length > 0) {
      return of(cur);
    }
    return this.api.getRefDataModules().pipe(
      tap((list) => this.refModules$.next(list)),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Modules');
        return of([]);
      })
    );
  }

  getModuleRef(moduleKey: string): RefDataModuleDto | undefined {
    const list = this.refModules$.value ?? [];
    const k = moduleKey.toLowerCase();
    const mapped = ROUTE_KEY_TO_REF_DATA_MODULE_CODE[k];
    const tryCodes = [mapped, moduleKey].filter((c): c is string => !!c && String(c).trim() !== '');
    const seen = new Set<string>();
    for (const code of tryCodes) {
      const low = code.toLowerCase();
      if (seen.has(low)) {
        continue;
      }
      seen.add(low);
      const hit = list.find((m) => String(m.code || '').toLowerCase() === low);
      if (hit) {
        return hit;
      }
    }
    // Repli : table_db = products / clients / …
    for (const code of tryCodes) {
      const low = code.toLowerCase();
      const byTable = list.find((m) => String(m.table_db || '').toLowerCase() === low);
      if (byTable) {
        return byTable;
      }
    }
    return undefined;
  }

  private ctxOrError(moduleKey: string): { ref: RefDataModuleDto; stationId: number; userId: number } | null {
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.error('Session invalide : station ou utilisateur introuvable.', 'Erreur');
      return null;
    }
    const ref = this.getModuleRef(moduleKey);
    if (!ref) {
      const list = this.refModules$.value ?? [];
      const codes = list.map((m) => m.code).join(', ');
      const tried = [ROUTE_KEY_TO_REF_DATA_MODULE_CODE[moduleKey.toLowerCase()], moduleKey]
        .filter((c): c is string => !!c)
        .filter((c, i, a) => a.indexOf(c) === i)
        .join(' » ou « ');
      const hint =
        list.length === 0
          ? ' Aucun module renvoyé par l’API (table vide, mauvaise URL, ou JSON non reconnu).'
          : ` Codes reçus : ${codes}.`;
      this.toastr.error(
        `Module « ${moduleKey} » introuvable (cherche « ${tried} »).${hint} ` +
          'Dans ref_data_module, la colonne code doit être p.ex. products pour la route produits.',
        'Erreur',
        { timeOut: 12000 }
      );
      return null;
    }
    return { ref, stationId, userId };
  }

  refreshAll(moduleKey: string): Observable<void> {
    return this.ensureRefModules().pipe(
      switchMap(() => {
        const ctx = this.ctxOrError(moduleKey);
        if (!ctx) {
          return of(void 0);
        }
        const { ref, stationId, userId } = ctx;
        return forkJoin({
          settings: this.api.getDataModuleSettings(stationId, ref.id).pipe(catchError(() => of(null))),
          usage: this.api.getDataModuleUsage(stationId, ref.id).pipe(catchError(() => of(null))),
          champs: this.api.getParamTableFields(stationId, userId, resolveRefModuleTableDbId(ref)).pipe(catchError(() => of([]))),
          regles: this.api.getParamRules(stationId, resolveRefModuleTableDbId(ref)).pipe(catchError(() => of([]))),
          histo: this.api
            .getParamAuditLogs(stationId, resolveRefModuleTableDbId(ref), { page: 1, page_size: 200 })
            .pipe(catchError(() => of({ rows: [] }))),
          imp: this.api.getDataImportFormats(stationId, userId, resolveRefModuleTableDbId(ref)).pipe(catchError(() => of([]))),
          exp: this.api.getDataExportFormats(stationId, userId, resolveRefModuleTableDbId(ref)).pipe(catchError(() => of([]))),
          dest: this.api.getDataExportDestinations(stationId, userId, resolveRefModuleTableDbId(ref)).pipe(catchError(() => of([])))
        }).pipe(
          tap((res) => {
            const champsUi = res.champs.map((row) => paramTableDtoToChamp(row));
            this.patchKey(this.champs, moduleKey, champsUi);

            const reglesUi = res.regles.map((r) => paramRuleDtoToModel(r));
            this.patchKey(this.regles, moduleKey, reglesUi);

            const histoUi = res.histo.rows.map((h) => auditDtoToRow(h));
            this.patchKey(this.historique, moduleKey, histoUi);

            if (res.usage) {
              this.patchKey(this.utilisationModule, moduleKey, usageDtoToFlags(res.usage));
            }

            const impUi: DonneesImportFormat[] = res.imp.map((x) => ({
              id: String(x.id),
              nom: x.nom,
              extension: x.extension,
              description: x.description ?? ''
            }));
            this.patchKey(this.importFormats, moduleKey, impUi);

            const expUi: DonneesExportFormat[] = res.exp.map((x) => ({
              id: String(x.id),
              nom: x.nom,
              type: x.type
            }));
            this.patchKey(this.exportFormats, moduleKey, expUi);

            this.patchKey(this.exportDestinations, moduleKey, res.dest);
          }),
          map(() => void 0)
        );
      })
    );
  }

  private patchKey<T>(sub: BehaviorSubject<Record<string, T>>, moduleKey: string, data: T): void {
    sub.next({ ...sub.value, [moduleKey]: data });
  }

  getChamps(moduleKey: string): DonneesChampModel[] {
    return cloneChamps(this.champs.value[moduleKey] ?? []).sort((a, b) => a.ordre - b.ordre);
  }

  setChamps(moduleKey: string, rows: DonneesChampModel[]): void {
    this.patchKey(this.champs, moduleKey, cloneChamps(rows));
  }

  saveChampApi(moduleKey: string, champ: DonneesChampModel, isNew: boolean): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return throwError(() => new Error('ctx'));
    }
    const { ref, stationId, userId } = ctx;
    const payload = champToParamTablePayload(champ, resolveRefModuleTableDbId(ref), stationId, userId);
    const req = isNew
      ? this.api.postParamTableField(payload)
      : this.api.patchParamTableField(Number(champ.id), payload);
    return req.pipe(
      switchMap((saved) => this.syncSelectParamsAfterChampSave(isNew, champ, saved)),
      switchMap(() => this.refreshChampsOnly(moduleKey)),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Champ');
        return throwError(() => err);
      })
    );
  }

  getSelectParamsForParamTable(paramTableId: number): Observable<SelectParamOptionDto[]> {
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0 || !Number.isFinite(paramTableId) || paramTableId <= 0) {
      return of([]);
    }
    return this.api.getSelectParamsByParamTable(paramTableId, stationId, userId).pipe(
      map((raw) => this.parseSelectParamsGetRows(raw)),
      catchError(() => of([]))
    );
  }

  private syncSelectParamsAfterChampSave(
    isNew: boolean,
    champ: DonneesChampModel,
    saved: unknown
  ): Observable<unknown> {
    if (champ.type !== 'liste') {
      return of(void 0);
    }
    if (isNew) {
      return this.postSelectParamsAfterNewListeIfNeeded(champ, saved);
    }
    return this.syncSelectParamsAfterListeEdit(champ);
  }

  private postSelectParamsAfterNewListeIfNeeded(champ: DonneesChampModel, saved: unknown): Observable<unknown> {
    const paramTableId = this.extractParamFieldIdFromSaveResponse(saved);
    const rawOpts = champ.liste?.valeurs ?? [];
    const parts = this.buildSelectParamsPostParts(rawOpts);
    if (paramTableId == null || parts.length === 0) {
      return of(void 0);
    }
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.error('Session invalide : station ou utilisateur introuvable (localStorage).', 'Options liste');
      return of(void 0);
    }
    return this.api
      .postSelectParamsMany(paramTableId, parts, stationId, userId)
      .pipe(
        catchError((err) => {
          this.toastr.error(this.msg(err), 'Options liste');
          return of(null);
        })
      );
  }

  private syncSelectParamsAfterListeEdit(champ: DonneesChampModel): Observable<unknown> {
    const paramTableId = Number(champ.id);
    if (!Number.isFinite(paramTableId) || paramTableId <= 0 || !champ.liste) {
      return of(void 0);
    }
    const { stationId, userId } = getParametrageSessionIds();
    if (stationId <= 0 || userId <= 0) {
      this.toastr.error('Session invalide : station ou utilisateur introuvable (localStorage).', 'Options liste');
      return of(void 0);
    }
    const vals = champ.liste.valeurs.filter((x) => (x.label || '').trim() && (x.value || '').trim());

    return this.getSelectParamsForParamTable(paramTableId).pipe(
      catchError(() => of([] as SelectParamOptionDto[])),
      switchMap((serverOpts) => {
        const byStableKey = new Map<string, number>();
        for (const s of serverOpts) {
          const key = this.selectParamStableKey(s.value, s.libelle);
          if (key && !byStableKey.has(key)) {
            byStableKey.set(key, s.id);
          }
        }

        const toPut: Array<{ id: number; label: string; value: string; couleur?: string }> = [];
        const toPost: DonneesListeValeur[] = [];

        for (const v of vals) {
          const idFromForm =
            v.selectParamId != null && Number(v.selectParamId) > 0 ? Math.floor(Number(v.selectParamId)) : null;
          const key = this.selectParamStableKey(v.value || '', v.label || '');
          const idFromServer = key ? byStableKey.get(key) : undefined;
          const id = idFromForm ?? idFromServer ?? null;
          if (id != null && id > 0) {
            toPut.push({
              id,
              label: (v.label || '').trim(),
              value: (v.value || '').trim(),
              couleur: (v.couleur || '').trim() || undefined
            });
          } else {
            toPost.push(v);
          }
        }

        const putRequests = toPut.map((row) => {
          const putBody: SelectParamsPutDto = { libelle: row.label };
          const code = row.value.trim();
          if (code) {
            putBody.code = code;
          }
          if (row.couleur) {
            putBody.couleur = row.couleur;
          }
          return this.api.putSelectParam(row.id, putBody, stationId, userId).pipe(
            catchError((err) => {
              this.toastr.error(this.msg(err), 'Option liste');
              return of(null);
            })
          );
        });

        const puts$ = putRequests.length > 0 ? forkJoin(putRequests) : of([] as unknown[]);

        return puts$.pipe(
          switchMap(() => {
            if (toPost.length === 0) {
              return of(null);
            }
            const parts = this.buildSelectParamsPostParts(toPost);
            return this.api.postSelectParamsMany(paramTableId, parts, stationId, userId).pipe(
              catchError((err) => {
                this.toastr.error(this.msg(err), 'Nouvelles options liste');
                return of(null);
              })
            );
          })
        );
      })
    );
  }

  private selectParamStableKey(code: string, libelle: string): string {
    const c = (code || '').trim();
    const l = (libelle || '').trim().toLowerCase();
    if (c) {
      return `c:${c}`;
    }
    if (l) {
      return `l:${l}`;
    }
    return '';
  }

  private parseSelectParamsGetRows(raw: unknown): SelectParamOptionDto[] {
    const list = this.unwrapSelectParamsList(raw);
    const out: SelectParamOptionDto[] = [];
    for (const item of list) {
      if (!item || typeof item !== 'object') {
        continue;
      }
      const o = item as Record<string, unknown>;
      const idRaw = o['id'] ?? o['select_param_id'];
      const id = Number(idRaw);
      if (!Number.isFinite(id) || id <= 0) {
        continue;
      }
      const libelle = String(o['libelle'] ?? o['label'] ?? o['nom'] ?? '').trim();
      const valueRaw = o['value'] ?? o['valeur'] ?? o['code'] ?? idRaw;
      const value = valueRaw != null && valueRaw !== '' ? String(valueRaw).trim() : String(id);
      const couleurRaw = o['couleur'] ?? o['color'];
      const couleur =
        couleurRaw != null && String(couleurRaw).trim() !== '' ? String(couleurRaw).trim() : undefined;
      if (!libelle && !value) {
        continue;
      }
      out.push({
        id,
        libelle: libelle || value,
        value: value || libelle,
        couleur
      });
    }
    return out;
  }

  private unwrapSelectParamsList(raw: unknown): unknown[] {
    if (Array.isArray(raw)) {
      return raw;
    }
    if (raw && typeof raw === 'object') {
      const o = raw as Record<string, unknown>;
      const cand =
        o['data'] ??
        o['rows'] ??
        o['items'] ??
        o['options'] ??
        o['select_params'] ??
        o['results'];
      if (Array.isArray(cand)) {
        return cand;
      }
      if (cand && typeof cand === 'object' && !Array.isArray(cand)) {
        const inner = (cand as Record<string, unknown>)['rows'] ?? (cand as Record<string, unknown>)['data'];
        if (Array.isArray(inner)) {
          return inner;
        }
      }
    }
    return [];
  }

  private extractParamFieldIdFromSaveResponse(saved: unknown): number | null {
    if (saved == null || typeof saved !== 'object') {
      return null;
    }
    const o = saved as Record<string, unknown>;
    let id: unknown = o['id'];
    if (id == null && o['data'] != null && typeof o['data'] === 'object' && !Array.isArray(o['data'])) {
      id = (o['data'] as Record<string, unknown>)['id'];
    }
    if (id == null || id === '') {
      return null;
    }
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
  }

  deleteChampApi(moduleKey: string, id: string): Observable<void> {
      const ctx = this.ctxOrError(moduleKey);
      if (!ctx) {
          return throwError(() => new Error('ctx'));
      }
      return this.api.deleteParamTableField(Number(id), ctx.stationId, ctx.userId).pipe(
      switchMap(() => this.refreshChampsOnly(moduleKey)),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Suppression');
        return throwError(() => err);
      })
    );
  }

  patchChampActifApi(moduleKey: string, row: DonneesChampModel, actif: boolean): Observable<void> {
      const ctx = this.ctxOrError(moduleKey);
      if (!ctx) {
          return throwError(() => new Error('ctx'));
      }
      return this.api
          .patchParamTableField(Number(row.id), {
              station_id: ctx.stationId,
              user_id: ctx.userId,
              actif
          })
          .pipe(
              switchMap(() => this.refreshChampsOnly(moduleKey)),
              map(() => void 0),
              catchError((err) => {
                  this.toastr.error(this.msg(err), 'Champ');
                  return throwError(() => err);
              })
          );
  }

  saveChampsBulkApi(moduleKey: string, champs: DonneesChampModel[]): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return throwError(() => new Error('ctx'));
    }
    const { ref, stationId, userId } = ctx;
    if (champs.length === 0) {
      return of(void 0);
    }
    return forkJoin(
      champs.map((c) =>
        this.api.patchParamTableField(
          Number(c.id),
          champToParamTablePayload(c, resolveRefModuleTableDbId(ref), stationId, userId)
        )
      )
    ).pipe(
      switchMap(() => this.refreshChampsOnly(moduleKey)),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Enregistrement');
        return throwError(() => err);
      })
    );
  }

  syncChamps(moduleKey: string): Observable<void> {
    return this.refreshChampsOnly(moduleKey);
  }

  private refreshChampsOnly(moduleKey: string): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(void 0);
    }
    const { ref, stationId, userId } = ctx;
    return this.api.getParamTableFields(stationId, userId, resolveRefModuleTableDbId(ref)).pipe(
      tap((rows) => {
        const champsUi = rows.map((r) => paramTableDtoToChamp(r));
        this.patchKey(this.champs, moduleKey, champsUi);
      }),
      map(() => void 0)
    );
  }

  getRegles(moduleKey: string): DonneesRegleModel[] {
    return [...(this.regles.value[moduleKey] ?? [])];
  }

  setRegles(moduleKey: string, rows: DonneesRegleModel[]): void {
    this.patchKey(this.regles, moduleKey, [...rows]);
  }

  saveRegleApi(moduleKey: string, r: DonneesRegleModel, isNew: boolean): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return throwError(() => new Error('ctx'));
    }
    const dto = regleToDto(r, ctx.stationId, resolveRefModuleTableDbId(ctx.ref));
    const req = isNew
      ? this.api.postParamRule(dto)
      : this.api.patchParamRule(Number(r.id), dto);
    return req.pipe(
      switchMap(() => this.refreshReglesOnly(moduleKey)),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Règle');
        return throwError(() => err);
      })
    );
  }

  deleteRegleApi(moduleKey: string, id: string): Observable<void> {
      const ctx = this.ctxOrError(moduleKey);
      if (!ctx) {
          return throwError(() => new Error('ctx'));
      }
      return this.api.deleteParamRule(Number(id), ctx.stationId).pipe(
      switchMap(() => this.refreshReglesOnly(moduleKey)),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Règle');
        return throwError(() => err);
      })
    );
  }

  private refreshReglesOnly(moduleKey: string): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(void 0);
    }
    return this.api.getParamRules(ctx.stationId, resolveRefModuleTableDbId(ctx.ref)).pipe(
      tap((rows) => {
        this.patchKey(
          this.regles,
          moduleKey,
          rows.map((x) => paramRuleDtoToModel(x))
        );
      }),
      map(() => void 0)
    );
  }

  getHistorique(moduleKey: string): DonneesHistoriqueRow[] {
    return [...(this.historique.value[moduleKey] ?? [])];
  }

  loadHistoriqueApi(moduleKey: string, q: Parameters<ParametreDonneesApiService['getParamAuditLogs']>[2]): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(void 0);
    }
    return this.api.getParamAuditLogs(ctx.stationId, resolveRefModuleTableDbId(ctx.ref), q).pipe(
      tap((res) => {
        this.patchKey(
          this.historique,
          moduleKey,
          res.rows.map((h) => auditDtoToRow(h))
        );
      }),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Historique');
        return of(void 0);
      })
    );
  }

  getImportFormats(moduleKey: string): DonneesImportFormat[] {
    return [...(this.importFormats.value[moduleKey] ?? [])];
  }

  getExportFormats(moduleKey: string): DonneesExportFormat[] {
    return [...(this.exportFormats.value[moduleKey] ?? [])];
  }

  getExportDestinations(moduleKey: string): DataExportDestinationDto[] {
    return [...(this.exportDestinations.value[moduleKey] ?? [])];
  }

  getUtilisationModule(moduleKey: string): DonneesUtilisationFlags {
    return { ...(this.utilisationModule.value[moduleKey] ?? defaultUsage()) };
  }

  patchUtilisationModule(moduleKey: string, patch: Partial<DonneesUtilisationFlags>): void {
    const cur = this.getUtilisationModule(moduleKey);
    this.patchKey(this.utilisationModule, moduleKey, { ...cur, ...patch });
  }

  saveModuleUsageApi(moduleKey: string, u: DonneesUtilisationFlags): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return throwError(() => new Error('ctx'));
    }
    const body = flagsToUsageDto(u, ctx.stationId, ctx.ref.id);
    return this.api.putDataModuleUsage(body).pipe(
      tap(() => this.patchKey(this.utilisationModule, moduleKey, u)),
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Utilisation');
        return throwError(() => err);
      })
    );
  }

  loadModuleSettingsApi(moduleKey: string): Observable<ReturnType<typeof settingsDtoToGeneral> | null> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(null);
    }
    return this.api.getDataModuleSettings(ctx.stationId, ctx.ref.id).pipe(
      map((d) => (d ? settingsDtoToGeneral(d) : null)),
      catchError(() => of(null))
    );
  }

  reloadImportFormats(moduleKey: string): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(void 0);
    }
    return this.api.getDataImportFormats(ctx.stationId, ctx.userId, resolveRefModuleTableDbId(ctx.ref)).pipe(
      tap((res) => {
        const impUi: DonneesImportFormat[] = res.map((x) => ({
          id: String(x.id),
          nom: x.nom,
          extension: x.extension,
          description: x.description ?? ''
        }));
        this.patchKey(this.importFormats, moduleKey, impUi);
      }),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }

  reloadExportFormats(moduleKey: string): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return of(void 0);
    }
    return this.api.getDataExportFormats(ctx.stationId, ctx.userId, resolveRefModuleTableDbId(ctx.ref)).pipe(
      tap((res) => {
        const expUi: DonneesExportFormat[] = res.map((x) => ({
          id: String(x.id),
          nom: x.nom,
          type: x.type
        }));
        this.patchKey(this.exportFormats, moduleKey, expUi);
      }),
      map(() => void 0),
      catchError(() => of(void 0))
    );
  }

  saveModuleSettingsApi(
    moduleKey: string,
    g: Parameters<typeof generalToSettingsDto>[0]
  ): Observable<void> {
    const ctx = this.ctxOrError(moduleKey);
    if (!ctx) {
      return throwError(() => new Error('ctx'));
    }
    const body = generalToSettingsDto(g, ctx.stationId, ctx.ref.id);
    return this.api.putDataModuleSettings(body).pipe(
      map(() => void 0),
      catchError((err) => {
        this.toastr.error(this.msg(err), 'Paramètres');
        return throwError(() => err);
      })
    );
  }

  private msg(err: unknown): string {
    const e = err as {
      error?: unknown;
      message?: string;
      status?: number;
      statusText?: string;
    };
    const ex = e?.error;
    if (typeof ex === 'string' && ex.trim()) {
      return ex.length > 400 ? `${ex.slice(0, 400)}…` : ex;
    }
    if (ex && typeof ex === 'object' && !Array.isArray(ex)) {
      const o = ex as Record<string, unknown>;
      const m = o['message'] ?? o['msg'] ?? o['error'];
      if (typeof m === 'string' && m.trim()) {
        return m;
      }
      const errs = o['errors'];
      if (Array.isArray(errs)) {
        return errs.map(String).join(' ; ');
      }
    }
    if (e?.status != null && e.statusText) {
      return `HTTP ${e.status} : ${e.statusText}`;
    }
    return e?.message || 'Erreur réseau ou serveur.';
  }

  private buildSelectParamsPostParts(
    rows: { label?: string; value?: string; couleur?: string }[]
  ): Array<{ libelle: string; code?: string; couleur?: string }> {
    const out: Array<{ libelle: string; code?: string; couleur?: string }> = [];
    for (const v of rows) {
      const libelle = (v.label || '').trim();
      if (!libelle) {
        continue;
      }
      const code = (v.value || '').trim();
      const couleur = (v.couleur || '').trim();
      const item: { libelle: string; code?: string; couleur?: string } = { libelle };
      if (code) {
        item.code = code;
      }
      if (couleur) {
        item.couleur = couleur;
      }
      out.push(item);
    }
    return out;
  }
}

function defaultUsage(): DonneesUtilisationFlags {
  return {
    promoDeclenchement: false,
    promoExclusion: false,
    promoRegroupement: false,
    tarifRegles: false,
    tarifGenerationPrix: false,
    reportingBi: false,
    reportingExports: false,
    workflowValidations: false,
    workflowAutomatisations: false,
    rechercheSimple: true,
    rechercheAvancee: false
  };
}
